import React, { useEffect, useMemo, useRef } from "react";

import { Icon, ProgressTracker } from "@braze/beacon-core";

import { navigateToStepDescription, navigateToStepParameters } from "@lib/agenticAi/sharedToolContracts";

import { useFeatureOn } from "@services/useFeatureOn";
import useI18nNamespace from "@services/useI18nNamespace";
import { useOperatorEntityId } from "@services/useOperatorEntityId";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { useAgentFullscreenMode } from "@components/AgenticAi/components/AgenticChatInterface/useAgentFullscreenMode";
import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay";

import type { BrazeProgressTrackerProps } from "./types";

export const BrazeProgressTracker = ({
  activeStep,
  stepConfigs,
  onStepClick,
  disableFutureSteps = true,
  variant,
  operatorDisabledReason,
}: BrazeProgressTrackerProps) => {
  const { t } = useI18nNamespace("braze-progress-tracker");
  const featureOn = useFeatureOn();
  const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });
  const fullscreenMode = useAgentFullscreenMode();
  const id = useOperatorEntityId("braze-progress-tracker");

  const onStepClickRef = useRef(onStepClick);
  onStepClickRef.current = onStepClick;
  const options = useMemo(() => {
    return stepConfigs.map((step, index) => {
      let isStepEnabled = Boolean(step.complete || step.hasError);
      if (!isStepEnabled) {
        const enabledUntil = stepConfigs[activeStep].complete ? activeStep + 1 : activeStep;
        isStepEnabled = index === 0 || index <= enabledUntil || !disableFutureSteps;
      }

      return {
        id: String(index),
        label: step.stepName,
        disabled: index === activeStep || !isStepEnabled,
      };
    });
  }, [activeStep, disableFutureSteps, stepConfigs]);

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const hasNavigableStep = options.some((option) => !option.disabled);

  useEffect(() => {
    // Don't expose step navigation while a full-screen editor (e.g. the email composer) is open.
    // The user must finish/close it first; navigating wizard steps from under it would unmount the
    // editor and discard their unsaved work. The editor surfaces its own "Done" tool to exit.
    if (!onStepClickRef.current || !hasNavigableStep || fullscreenMode !== null) {
      return;
    }

    const cleanup = registerChangeTool({
      toolEntityId: id,
      name: "navigate_to_step",
      description: navigateToStepDescription,
      parameters: navigateToStepParameters,
      callback: ({ stepIndex }: { stepIndex: number }) => {
        const selectedOption = optionsRef.current[stepIndex];
        if (selectedOption === undefined) {
          return `Invalid step index: ${stepIndex}. Valid indices are 0 to ${optionsRef.current.length - 1}.`;
        }
        if (selectedOption.disabled) {
          const validIndices = optionsRef.current
            .filter((option) => !option.disabled)
            .map((option) => option.id)
            .join(", ");
          return `Step index ${stepIndex} is not available. Valid indices are: ${validIndices}.`;
        }
        onStepClickRef.current(stepIndex);
        return `Navigated to step "${selectedOption.label}" (index ${stepIndex}).`;
      },
      options: {
        toolGroup: "Agentic Creation & Editing",
        labels: {
          ready: t("navigate-step-ready", "Preparing to navigate"),
          running: t("navigate-step-running", "Navigating"),
          complete: t("navigate-step-complete", "Navigated"),
          failed: t("navigate-step-failed", "Failed to navigate"),
          stopped: t("navigate-step-stopped", "Stopped navigating"),
        },
        requiresApproval: "yes",
        argumentsSummaryText: ({ stepIndex }: { stepIndex: number }) => {
          return optionsRef.current[stepIndex]?.label || String(stepIndex);
        },
        renderApproval: ({ args: { stepIndex } }: { args: { stepIndex: number } }) => {
          const stepName = optionsRef.current[stepIndex]?.label || String(stepIndex);
          return (
            <DiffDisplay
              title={t("action-title", "Action")}
              modified={t("navigate-step-description", "Navigate to: {stepName}", { stepName })}
            />
          );
        },
        toolIcon: <Icon name="list-ol" />,
      },
    });

    return () => {
      cleanup();
    };
  }, [featureOn, registerChangeTool, id, t, fullscreenMode, hasNavigableStep]);

  const operatorDescription = useMemo(() => {
    const select = document.createElement("select");
    const activeStepId = String(activeStep);
    const currentOption = options.find((option) => option.id === activeStepId);
    const unavailableOptions = options.filter((option) => option.disabled && option.id !== activeStepId);

    select.id = id;
    select.setAttribute("value", activeStepId);
    if (currentOption) {
      select.setAttribute("data-current-step", `${currentOption.id}: ${currentOption.label}`);
    }
    if (unavailableOptions.length > 0) {
      select.setAttribute(
        "data-unavailable-options",
        unavailableOptions.map((option) => `${option.id}: ${option.label} (disabled)`).join(", ")
      );
    }
    options.forEach((option) => {
      if (option.disabled) {
        return;
      }
      const optionElement = document.createElement("option");
      optionElement.value = option.id;
      optionElement.textContent = option.label;
      select.appendChild(optionElement);
    });
    return select.outerHTML;
  }, [id, options, activeStep]);

  return (
    <div style={{ display: "contents" }} data-operator-description={operatorDescription}>
      <ProgressTracker
        activeStep={activeStep}
        stepConfigs={stepConfigs}
        onStepClick={onStepClick}
        variant={variant}
        disableFutureSteps={disableFutureSteps}
      />
    </div>
  );
};
